import { useState, ReactNode, useMemo } from 'react';
import { default as md5function } from 'md5';
import qs from 'qs';
import isRetina from 'is-retina';
import { AvatarSize, Email } from './Avatar';
import { cn } from '../../utils';

type GravatarProps = {
    email?: Email;
    md5?: string;
    rating?: string;
    defaultStyle?: string;
    style?: object;
    fallback: ReactNode;
    size?: AvatarSize;
    show: boolean;
};

export default function Gravatar({
    email,
    md5,
    size = 'md',
    rating = 'g',
    defaultStyle = 'retro',
    show,
    style,
    fallback,
    ...rest
}: GravatarProps) {
    const [display, setDisplay] = useState('image');

    // Gravatar service currently trims and lowercases all registered emails
    const formattedEmail = ('' + email).trim().toLowerCase();
    const md5Email = useMemo(
        () => md5function(formattedEmail, { encoding: 'binary' }),
        [formattedEmail],
    );

    const handleErrors = () => {
        if (fallback) {
            setDisplay('fallback');
        }
    };

    if (display === 'fallback') {
        // Fall back to another default component if the image results in a 404
        return <>{fallback}</>;
    } else {
        const base = `https://www.gravatar.com/avatar/`;

        const gravatarLookup = {
            sm: 24,
            md: 32,
            lg: 40,
            xl: 48,
        };
        const query = qs.stringify({
            s: gravatarLookup[size],
            r: rating,
            d: defaultStyle,
        });

        const retinaQuery = qs.stringify({
            s: gravatarLookup[size] * 4,
            r: rating,
            d: defaultStyle,
        });

        let hash;
        if (md5) {
            hash = md5;
        } else if (typeof email === 'string') {
            hash = md5Email;
        } else {
            console.warn(
                'Gravatar image can not be fetched. Either the "email" or "md5" prop must be specified.',
            );
            return <>{fallback}</>;
        }

        const src = `${base}${hash}?${query}`;
        const retinaSrc = `${base}${hash}?${retinaQuery}`;

        let modernBrowser = true; // server-side, we render for modern browsers

        if (typeof window !== 'undefined') {
            // this is not NodeJS
            modernBrowser = 'srcset' in document.createElement('img');
        }

        const combinedClassName = cn(
            'react-gravatar alli-rounded-full group-hover:alli-brightness-90 group-active:alli-opacity-60',
            {
                'alli-brightness-90': show,
            },
        );

        return (
            <div className="alli-rounded-full" data-testid="Gravatar">
                {!modernBrowser && isRetina() ? (
                    <img
                        alt={`Gravatar for ${formattedEmail}`}
                        style={style}
                        src={retinaSrc}
                        height={gravatarLookup[size]}
                        width={gravatarLookup[size]}
                        {...rest}
                        className={combinedClassName}
                        onError={handleErrors}
                    />
                ) : (
                    <img
                        alt={`Gravatar for ${formattedEmail}`}
                        style={style}
                        src={src}
                        srcSet={`${retinaSrc} 2x`}
                        height={gravatarLookup[size]}
                        width={gravatarLookup[size]}
                        {...rest}
                        className={combinedClassName}
                        onError={handleErrors}
                    />
                )}
            </div>
        );
    }
}
